import type { KeyValueStore, LocalStore, RecordStore } from "../platform-io.js";

/**
 * In-memory LocalStore. Used as a fallback (e.g. tests, SSR) and as the base
 * for the web IndexedDB store before hydration. Not persistent on its own.
 */
export class MemoryKeyValueStore implements KeyValueStore {
  private readonly map = new Map<string, unknown>();

  async get<T = unknown>(key: string): Promise<T | undefined> {
    return this.map.get(key) as T | undefined;
  }
  async set<T = unknown>(key: string, value: T): Promise<void> {
    this.map.set(key, value);
  }
  async delete(key: string): Promise<void> {
    this.map.delete(key);
  }
  async keys(prefix?: string): Promise<string[]> {
    const all = [...this.map.keys()];
    return prefix ? all.filter((k) => k.startsWith(prefix)) : all;
  }
}

export class MemoryRecordStore implements RecordStore {
  private readonly collections = new Map<string, Map<string, unknown>>();

  private col(name: string): Map<string, unknown> {
    let c = this.collections.get(name);
    if (!c) {
      c = new Map();
      this.collections.set(name, c);
    }
    return c;
  }

  async put(collection: string, id: string, record: unknown): Promise<void> {
    this.col(collection).set(id, record);
  }
  async get<T = unknown>(collection: string, id: string): Promise<T | undefined> {
    return this.col(collection).get(id) as T | undefined;
  }
  async all<T = unknown>(collection: string): Promise<T[]> {
    return [...this.col(collection).values()] as T[];
  }
  async remove(collection: string, id: string): Promise<void> {
    this.col(collection).delete(id);
  }
  async clear(collection: string): Promise<void> {
    this.col(collection).clear();
  }
}

export function createMemoryLocalStore(): LocalStore {
  return { kv: new MemoryKeyValueStore(), records: new MemoryRecordStore() };
}
